import axios from "axios";
import { useInfiniteQuery } from "react-query";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { axiosInstance } from "../api/axios";
import {
    BLOB_ACCOUNT_IMAGE,
    BLOB_BOIMAGE_CONTAINER,
    BLOB_BRAND_IMAGE,
    BLOB_COMPANY_IMAGE,
    BLOB_PRODUCT_CONTAINER,
    BLOB_STAFF_CONTAINER,
    BLOB_STORE_CONTAINER
} from "../config/constant";
import toastError from "./../assets/images/toast-error.png";
import toastInfo from "./../assets/images/toast-info.png";
import toastSuccess from "./../assets/images/toast-success.png";
import toastWarning from "./../assets/images/toast-warning.png";
import { resetConfirmationPopup } from "../PopupContext";

/* #region MAIN FUNCTIONS */

/** Displays a toast message with the specified options.
 *
 * @param {Object} props - The properties of the toast message.
 * @param {string} props.title - The title of the toast message.
 * @param {string} props.description - The description of the toast message.
 * @param {string} props.message - The message of the toast.
 * @param {string} [props.type="success"] - The type of the toast message. Can be "success", "warning", "error", or "info".
 * @param {number} [props.autoClose=5000] - The time in milliseconds before the toast message automatically closes.
 * @param {function} props.onClose - The function to call when the toast message closes.
 */
export function showToastMessage({
    title,
    description,
    message,
    type = "success",
    autoClose = 5000,
    onClose = undefined
}) {
    const descriptionStr = description || message;

    const htmlBody = descriptionStr ? (
        <div>
            <div>{title}</div>
            <div
                style={{ fontSize: "12px", color: "var(--text-icons-gray-100, #D1D1D1)", marginTop: "5px" }}
                dangerouslySetInnerHTML={{ __html: descriptionStr }}
            />
        </div>
    ) : (
        title
    );

    switch (type) {
        case "success":
            toast.success(htmlBody, {
                theme: "dark",
                icon: () => <img alt="success" src={toastSuccess} />,
                progressStyle: { background: "#00BB00" },
                autoClose: autoClose,
                onClose: onClose ? onClose : () => {}
            });
            return;
        case "warning":
            toast.warning(htmlBody, {
                theme: "dark",
                icon: () => <img alt="warning" src={toastWarning} />,
                progressStyle: { background: "#ff9900" },
                autoClose: autoClose,
                onClose: onClose ? onClose : () => {}
            });
            return;
        case "error":
            toast.error(htmlBody, {
                theme: "dark",
                icon: () => <img alt="error" src={toastError} />,
                progressStyle: { background: "#FF3636" },
                autoClose: autoClose,
                onClose: onClose ? onClose : () => {}
            });
            return;
        case "info":
            toast.info(htmlBody, {
                theme: "dark",
                icon: () => <img alt="info" src={toastInfo} />,
                progressStyle: { background: "#00AEEF" },
                autoClose: autoClose,
                onClose: onClose ? onClose : () => {}
            });
            return;
        default:
            console.error("Invalid type provided for showToastMessage method!");
            return;
    }
}

/**
 * Displays a generic confirmation popup with customizable options and callbacks.
 *
 * @param {Object} options - Configuration options for the confirmation popup.
 * @param {Function} options.setGenericConfirmationPopup - REQUIRED: Function to set the popup state.
 * @param {boolean} [options.show=true] - REQUIRED: Whether to show the popup.
 * @param {string} options.useIconSrc - Icon above title.
 * @param {string} options.title - REQUIRED: Title of the popup.
 * @param {string} options.messageHtml - REQUIRED: HTML content for the popup message.
 * @param {Function} [options.onConfirmClicked] - REQUIRED: Callback for confirm button click.
 * @param {Function} [options.onCancelClicked] - REQUIRED: Callback for cancel button click.
 * @param {string} [options.headingText="JUST CHECKING"] - Heading text.
 * @param {string} [options.headingColor="var(--text-icons-green-500, #0B0)"] - Heading color.
 * @param {boolean} [options.showConfirm=true] - Whether to show the confirm button.
 * @param {string} [options.confirmText="CONFIRM"] - Confirm button text.
 * @param {string} [options.confirmTextColor] - Confirm button text color.
 * @param {boolean} [options.showCancel=true] - Whether to show the cancel button.
 * @param {string} [options.cancelText="CANCEL"] - Cancel button text.
 * @param {string} [options.cancelTextColor] - Cancel button text color.
 * @param {string} [options.thirdButtonText="SEND"] - Third button text.
 * @param {string} [options.thirdButtonTextColor] - Third button text color.
 * @param {string} [options.topButtonText="TOP"] - Top button text.
 * @param {string} [options.topButtonTextColor] - Top button text color.
 * @param {boolean} [options.showThirdButton=false] - Whether to show the third button.
 * @param {boolean} [options.showTopButton=false] - Whether to show the top button.
 * @param {Function} [options.onThirdButtonClicked] - Callback for third button click.
 * @param {Function} [options.onTopButtonClicked] - Callback for top button click.
 * @param {boolean} [options.afterConfirmWillResetPopup=true] - Reset popup after confirm.
 * @param {boolean} [options.afterCancelWillResetPopup=true] - Reset popup after cancel.
 * @param {boolean} [options.afterThirdButtonWillResetPopup=true] - Reset popup after third button.
 * @param {boolean} [options.afterTopButtonWillResetPopup=true] - Reset popup after top button.
 * @param {string} [options.headingFontSize="18px"] - Font size for heading.
 * @param {boolean} [options.useAlternativeLayout] - Use alternative layout.
 * @param {boolean} [options.loading=false] - Show loading state.
 * @param {string} [options.loadingHeading] - Loading heading text.
 * @param {string} [options.cancelState="secondary"] - Cancel button state.
 * @param {string} [options.confirmState="primary"] - Confirm button state.
 * @param {string} [options.thirdButtonState="primary"] - Third button state.
 * @param {string} [options.topButtonState="primary"] - Top button state.
 * @param {Object} [options.useImageHeader] - Image header configuration.
 * @param {string} [options.useImageHeader.src] - Image source URL.
 * @param {string} [options.useImageHeader.width="100%"] - Image width.
 * @param {string} [options.buttonWidth] - Button width.
 * @param {string} [options.thirdButtonWidth] - Third button width.
 * @param {string} [options.confirmWidth] - Confirm button width.
 * @param {string} [options.cancelWidth] - Cancel button width.
 * @param {string} [options.buttonFontSize] - Button font size.
 * @param {boolean} [options.showCloseButton=false] - Whether to show close button.
 * @param {Function} [options.onClickCloseBtn] - Callback for close button click.
 * @param {React.ReactNode} [options.customLoader] - Custom loader component.
 * @param {string} [options.width="360px"] - Popup width.
 * @param {string} [options.loaderBgColor="#FF9900"] - Loader background color.
 * @param {string} [options.titleColor="var(--text-icons-gray-100, #d1d1d1)"] - Title color.
 * @param {string} [options.customCSS=""] - Custom CSS for popup.
 * @param {boolean} [options.useV2=false] - Use DialogBoxComponentV2 instead of DialogBoxComponent.
 */
export function showConfirmationMessage({
    //Required parameters:
    setGenericConfirmationPopup,
    show = true,
    useIconSrc = undefined,
    title,
    messageHtml,
    onConfirmClicked = undefined,
    onCancelClicked = undefined,
    //Optional parameters:
    headingText = "JUST CHECKING",
    headingColor = "var(--text-icons-green-500, #0B0)",
    showConfirm = true,
    confirmText = "CONFIRM",
    showCancel = true,
    cancelText = "CANCEL",
    thirdButtonText = "SEND",
    topButtonText = "TOP",
    showThirdButton = false,
    showTopButton = false,
    onThirdButtonClicked = undefined,
    onTopButtonClicked = undefined,
    afterConfirmWillResetPopup = true,
    afterCancelWillResetPopup = true,
    afterThirdButtonWillResetPopup = true,
    afterTopButtonWillResetPopup = true,
    // Least used parameters:
    headingFontSize = "18px",
    useAlternativeLayout = undefined,
    loading = false,
    loadingHeading = undefined,
    cancelState = "secondary",
    confirmState = "primary",
    thirdButtonState = "primary",
    topButtonState = "primary",
    useImageHeader = {
        src: undefined,
        width: "100%"
    },
    buttonWidth = undefined,
    thirdButtonWidth = undefined,
    confirmWidth = undefined,
    cancelWidth = undefined,
    buttonFontSize = undefined,
    showCloseButton = false,
    onClickCloseBtn = () => {},
    customLoader = undefined,
    width = "360px",
    loaderBgColor = "var(--Buttons-Green-Outline)",
    titleColor = "var(--text-icons-gray-100, #d1d1d1)",
    customCSS = "",
    confirmTextColor = undefined,
    cancelTextColor = undefined,
    thirdButtonTextColor = undefined,
    topButtonTextColor = undefined,
    useV2 = false,

    // Deprecated parameters:
    message,
    onClickYes = undefined,
    onClickNo = undefined
}) {
    /* #region  RESET METHODS */
    let handleConfirmClicked = onConfirmClicked || onClickYes;
    let handleCancelClicked = onCancelClicked || onClickNo;
    let handleThirdButtonClicked = onThirdButtonClicked;
    let handleTopButtonClicked = onTopButtonClicked;

    if (!setGenericConfirmationPopup) {
        showToastMessage({
            type: "error",
            title: "ShowConfirmationMessage requires setGenericConfirmationPopup!",
            description: `Confirmation title of called popup was: "${title}"`,
            timeout: 10000
        });
        return;
    }

    if (afterConfirmWillResetPopup) {
        handleConfirmClicked = () => {
            resetConfirmationPopup(setGenericConfirmationPopup);
            if (onConfirmClicked) onConfirmClicked();
            else if (onClickYes) onClickYes();
        };
    }

    if (afterCancelWillResetPopup) {
        handleCancelClicked = () => {
            resetConfirmationPopup(setGenericConfirmationPopup);
            if (onCancelClicked) onCancelClicked();
            else if (onClickNo) onClickNo();
        };
    }

    if (afterThirdButtonWillResetPopup) {
        handleThirdButtonClicked = () => {
            resetConfirmationPopup(setGenericConfirmationPopup);
            if (onThirdButtonClicked) onThirdButtonClicked();
        };
    }

    if (afterTopButtonWillResetPopup) {
        handleTopButtonClicked = () => {
            resetConfirmationPopup(setGenericConfirmationPopup);
            if (onTopButtonClicked) onTopButtonClicked();
        };
    }
    /* #endregion */

    setGenericConfirmationPopup({
        show,

        headingColor: headingColor,
        heading: headingText,
        headingFontSize: headingFontSize,
        useIconSrc: useIconSrc,
        title: title,
        message: messageHtml ? messageHtml : message,
        useAlternativeLayout: useAlternativeLayout,
        onClickYes: handleConfirmClicked,
        onClickNo: handleCancelClicked,
        loading: loading,
        loadingHeading: loadingHeading,
        cancelText: cancelText,
        confirmText: confirmText,
        cancelState: cancelState,
        confirmState: confirmState,
        thirdButtonState: thirdButtonState,
        showCancel: showCancel,
        showConfirm: showConfirm,
        useImageHeader: useImageHeader,
        buttonWidth: buttonWidth,
        thirdButtonWidth: thirdButtonWidth,
        confirmWidth: confirmWidth,
        cancelWidth: cancelWidth,
        buttonFontSize: buttonFontSize,
        showCloseButton: showCloseButton,
        onClickCloseBtn: onClickCloseBtn,
        customLoader: customLoader,
        showTopButton: showTopButton,
        topButtonText: topButtonText,
        topButtonState: topButtonState,
        onClickTopButton: handleTopButtonClicked,
        width: width,
        showThirdButton: showThirdButton,
        thirdButtonText: thirdButtonText,
        onClickThirdButton: handleThirdButtonClicked,
        loaderBgColor: loaderBgColor,
        titleColor: titleColor,
        customCSS: customCSS,
        confirmTextColor: confirmTextColor,
        cancelTextColor: cancelTextColor,
        thirdButtonTextColor: thirdButtonTextColor,
        topButtonTextColor: topButtonTextColor,
        useV2: useV2
    });
}

/** Makes an API request to the specified endpoint with the provided data.
 * @param {Object} params - The parameters for the API request.
 * @param {string} params.endpoint - The API endpoint to call. If endpoint has a slash then will consider this is a BOCommon endpoint.
 * @param {Object} params.data - The data to send in the request body.
 * @param {string} [params.method="POST"] - The HTTP method to use for the request (supported: "GET", "POST", "PUT", "DELETE").
 * @param {function} [params.setLoader=null] - A function to show a loader while the request is in progress.
 * @param {boolean} [params.onErrorShowMsg=true] - Whether to show an error message if the request fails. Will show by default.
 * @param {function} [params.onError] - A function to call if an error occurs during the request.
 * @return {Promise} Resolves with the Axios response for completed backend requests,
 * including domain errors after centralized handling. Transport errors are handled
 * through the standard toast and onError callback and intentionally leave the Promise
 * pending for compatibility.
 */
export function makeApiRequest({ endpoint, data, method = "POST", setLoader = null, onErrorShowMsg = true, onError }) {
    return new Promise(resolve => {
        let api_url = `${process.env.REACT_APP_API_URL}${endpoint}?code=${process.env.REACT_APP_API_CODE}`;
        let requestData = data;

        // Check if the endpoint is a BOCommon endpoint (contains a slash)
        const hasSlash = endpoint.includes("/");
        const useBOstageX = endpoint.toLowerCase().startsWith("x/");
        const useBOCommon = hasSlash && !useBOstageX;

        // If using BOCommon backend instead, we need to use the common endpoint and pass the context
        if (useBOCommon) {
            const BOCommonOperation = endpoint.split("/")[0];
            const BOCommonEndpoint = endpoint.split("/")[1];

            const commonEndpoint = "callCommonAPI";
            api_url = `${process.env.REACT_APP_API_URL}${commonEndpoint}?code=${process.env.REACT_APP_API_CODE}`;
            requestData = {
                context: BOCommonOperation,
                endpoint: BOCommonEndpoint,
                paramsData: { ...data }
            };
        } else if (useBOstageX) {
            //const BOstageXOperation = endpoint.split("/")[0];
            const BOstageXOperation = endpoint.split("/")[1];
            const BOstageXEndpoint = endpoint.split("/")[2];

            const boStageXEndpoint = "callBOstageXapi";
            api_url = `${process.env.REACT_APP_API_URL}${boStageXEndpoint}?code=${process.env.REACT_APP_API_CODE}`;
            requestData = {
                context: BOstageXOperation,
                endpoint: BOstageXEndpoint,
                paramsData: { ...data }
            };
        }

        // Start the loader
        if (setLoader) setLoader(true);

        let axiosPromise;

        // Assign axiosPromise based on HTTP method
        switch (method.toUpperCase()) {
            case "GET":
                axiosPromise = axiosInstance.get(api_url, { params: requestData });
                break;
            case "POST":
                axiosPromise = axiosInstance.post(api_url, requestData);
                break;
            case "PUT":
                axiosPromise = axiosInstance.put(api_url, requestData);
                break;
            case "DELETE":
                axiosPromise = axiosInstance.delete(api_url, { data: requestData });
                break;
            default:
                throw new Error(`Unsupported HTTP method: ${method}`);
        }

        // Now chain the handlers
        axiosPromise
            .then(response => {
                if (response.data?.status === 200) {
                    resolve(response);
                } else {
                    const message =
                        response.data?.message && typeof response.data?.message === "string"
                            ? response.data?.message
                            : "An error occurred while trying to process your request.";
                    if (onErrorShowMsg) showToastMessage({ title: message, type: "error" });
                    if (onError) onError(response);
                    resolve(response);
                }
            })
            .catch(error => {
                if (onError) onError(error);
                const message = error?.message
                    ? `${error.message}.`
                    : "An error occurred while trying to process your request.";
                if (onErrorShowMsg) showToastMessage({ title: message, type: "error" });
                // Intentionally do not reject or resolve here. This is an established
                // frontend/backend compatibility contract; see docs/documentation.md.
            })
            .finally(() => {
                // Stop the loader
                if (setLoader) {
                    setTimeout(() => {
                        setLoader(false);
                    }, 0);
                }
            });
    });
}

/** Fetches the user's IP address using the ipify API. */
export async function getUserIP() {
    try {
        const res = await axios.get("https://api.ipify.org/?format=json");
        return res?.data?.ip;
    } catch (error) {
        console.error("Error fetching user IP:", error);
        return undefined;
    }
}

/** Opens a new page in a new tab with the specified URL.
 * @param {string} url - The URL of the page to open.
 * @returns {void}
 */
export function openNewPage(url) {
    if (!url) {
        showToastMessage({
            title: "Can't open new page, URL is not defined.",
            type: "error",
            autoClose: 5000
        });

        return;
    }

    window.open(url, "_blank");
}

/** Strips HTML tags from a given string.
 * @param {string} html - The HTML string to strip tags from.
 * @returns {string} The string with HTML tags removed.
 */
export function stripHtmlTags(html) {
    return html?.replace(/<\/?[^>]+(>|$)/g, "");
}

/** Checks if a functionality is only available in localhost. Useful for debugging purposes.
 * @param {string} functionality - The name of the functionality to check.
 * @returns {boolean} Returns true in localhost, false otherwise.
 * If the debugPass is set to "gamechanger", it will return false regardless of the hostname.
 */
export function isLocalhost(functionality, ignoreLog = false) {
    const isLocalhost =
        window.location.hostname === "localhost" ||
        ["192.168.", "10.", "127.0."].some(prefix => window.location.hostname.startsWith(prefix));

    //search if functionality is found in the localStorage with prefix "debugPass_{functionality}" as true
    //example: functionality= "promptAIchat" => localStorage.debugPass_promptAIchat = "true"
    const debugPassActive =
        localStorage.getItem(`debugPass_${functionality}`) === "true" ||
        localStorage.getItem(`debugPass_${functionality}`) === true;
    if (debugPassActive) return false;

    if (localStorage.getItem("debugPass") === "gamechanger") return false;
    if (functionality === "Logout Timer") return true;

    if (functionality && !ignoreLog) console.log(`NOTE: Functionality only available in localhost: ${functionality}`);
    else if (!functionality)
        console.error("NOTE: Functionality only available in localhost, but no functionality name provided.");
    return isLocalhost;
}

/** Checks if the user is an admin debug user. Useful for debugging purposes.
 * @returns {boolean} Returns true if the user is an admin debug user, false otherwise.
 */
export function isAdminDebug() {
    return localStorage.getItem("adminDebug") === "true" || localStorage.getItem("adminDebug") === true;
}

/** Scrolls the window to the top of the page. (By default the class to scroll is "library-scrollable")
 * @param {boolean} smooth - If true, the scroll will be smooth. Defaults to false.
 * @param {string} [classToScroll="library-scrollable"] - The class name of the element to scroll. Defaults to "library-scrollable".
 */
export function scrollWindowToTop(smooth = false, classToScroll = "library-scrollable") {
    //scroll all occurrences of the class "classToScroll"
    const scrollableClasses = document.querySelectorAll(`.${classToScroll}`);
    scrollableClasses.forEach(scrollableClass => {
        scrollableClass.scrollTo({ top: 0, behavior: smooth ? "smooth" : "auto" });
    });
}

/** Custom hook to perform an infinite query using a POST request.
 * @param {string|Array} queryKey - The query key or an array of query keys for the query.
 * @param {string} api - The API endpoint to call.
 * @param {Object} formData - The data to send in the request body.
 * @param {function} onSuccess - Callback function to execute on successful query.
 * @param {boolean} useBOCommon - Whether to use the BOCommon API operation.
 * @param {string} BOCommonOperation - The BOCommon operation to perform if useBOCommon is true.
 * @param {boolean} keepPreviousData - Whether to keep previous data while fetching new data.
 * @param {boolean} enabled - Whether the query is enabled or not.
 * @returns {Object} The result of the infinite query.
 */
export const usePostInfiniteQuery = (
    queryKey,
    api,
    formData,
    onSuccess,
    useBOCommon,
    BOCommonOperation,
    keepPreviousData = false,
    enabled = true
) => {
    const queryKeyArray = typeof queryKey === "string" ? [queryKey] : queryKey;
    return useInfiniteQuery({
        queryKey: queryKeyArray,
        queryFn: async ({ pageParam = 1 }) => {
            formData.page = pageParam;

            let tmp = null;
            if (!localStorage.getItem("token")) {
                console.error("Request cancelled, user not logged in.");
                return [];
            }
            if (!useBOCommon) tmp = await makePostRequest(api, formData);
            else tmp = await makeCommonPostRequest(BOCommonOperation, api, formData);
            const res = tmp?.data;
            if (res?.status !== 200) {
                /* if (![undefined, "ok", "success", ""].includes(res?.message?.toLowerCase()))
                    showErrorMessage(res?.message); */
                return [];
            }

            return res;
        },

        getNextPageParam: (lastPage, pages) => {
            return lastPage?.data?.nextPage;
        },
        onSuccess: onSuccess,
        keepPreviousData: keepPreviousData,
        enabled: enabled
    });
};

/** Removes queries (cached data) from the query client associated with the provided query key(s).
 * @param {Object} queryClient - The query client instance.
 * @param {string|Array} queryKey - The query key string or an array of query keys to remove.
 */
export const removeQueries = (queryClient, queryKey) => {
    if (!queryClient || !queryKey) console.error("Library.removeQueries: queryClient or queryKey is not defined.");
    const queryKeyArray = typeof queryKey === "string" ? [queryKey] : queryKey;

    if (queryKeyArray?.length > 0) {
        queryKeyArray?.forEach(key => {
            if (key) {
                queryClient?.removeQueries(key);
            }
        });
    }
};

/* #endregion */

/* #region DEPRECATED FUNCTIONS */

/** @deprecated use showToastMessage instead */
export function showSuccessMessage(message, autoClose = 5000, onClose = () => {}) {
    showToastMessage({
        title: message,
        type: "success",
        autoClose: autoClose,
        onClose: onClose
    });
}

/** @deprecated use showToastMessage instead */
export function showWarningMessage(message, autoClose = 5000, onClose = () => {}) {
    showToastMessage({
        title: message,
        type: "warning",
        autoClose: autoClose,
        onClose: onClose
    });
}

/** @deprecated use showToastMessage instead */
export function showErrorMessage(message, autoClose = 5000, onClose = () => {}) {
    showToastMessage({
        title: message,
        type: "error",
        autoClose: autoClose,
        onClose: onClose
    });
}

/** @deprecated this didnt got to be a standard */
export function showUpdateMessageAndGoBack(navigate, onClose = () => {}) {
    const message = "Changes saved successfully!";
    const subMessage = "You can now proceed.";
    const autoClose = 5000;

    toast.success(
        <div>
            <div>{message}</div>
            <div style={{ fontSize: "12px", color: "var(--text-icons-gray-100, #D1D1D1)", marginTop: "5px" }}>
                {subMessage}
            </div>
        </div>,
        {
            theme: "dark",
            icon: ({ theme, type }) => <img alt="success" src={toastSuccess} />,
            autoClose: autoClose,
            onClose: () => {
                if (onClose) onClose();
            }
        }
    );
    if (navigate) navigate(-1);
    else console.error("Navigation function not provided to showUpdateMessageAndGoBack method.");
}

/** @deprecated use makeApiRequest instead */
export function makePostRequest(
    endpoint,
    data,
    useFormData = false,
    setShowLoader = null,
    onError = () => {},
    onUploadProgress = undefined,
    onErrorShowMsg = true
) {
    return new Promise((resolve, reject) => {
        const api_url = `${process.env.REACT_APP_API_URL}${endpoint}?code=${process.env.REACT_APP_API_CODE}`;

        /* let axiosConfig = !useFormData
            ? {}
            : {
                  "Content-Type": "multipart/form-data",
                  "Access-Control-Allow-Origin": "*" 
              }; */

        if (setShowLoader) setShowLoader(true);
        //if (onUploadProgress) axiosConfig = { ...axiosConfig, onUploadProgress: onUploadProgress };

        axiosInstance
            .post(api_url, data)
            .then(response => {
                if (response.data?.status === 200) {
                    resolve(response);
                } else {
                    const message =
                        response.data?.message && typeof response.data?.message === "string"
                            ? response.data?.message
                            : "An error occurred while trying to process your request.";
                    if (onErrorShowMsg) showErrorMessage(message);
                    onError(response);
                    resolve(response);
                }
            })
            .catch(error => {
                onError(error);
                const message = error?.message
                    ? `${error.message}.`
                    : "An error occurred while trying to process your request.";
                if (onErrorShowMsg) showErrorMessage(message);
                //reject(error);
            })
            .finally(() => {
                if (setShowLoader) {
                    setTimeout(() => {
                        setShowLoader(false);
                    }, 0);
                }
            });
    });
}

/** @deprecated use makeApiRequest instead */
export function makeCommonPostRequest(
    context,
    endpoint,
    data = {},
    useFormData = false,
    setShowLoader = null,
    onError = () => {},
    onUploadProgress = undefined,
    onErrorShowMsg = true
) {
    const commonEndpoint = "callCommonAPI";
    const params = {
        context: context,
        endpoint: endpoint,
        paramsData: { ...data }
    };

    return new Promise((resolve, reject) => {
        const api_url = `${process.env.REACT_APP_API_URL}${commonEndpoint}?code=${process.env.REACT_APP_API_CODE}`;

        /* let axiosConfig = !useFormData
            ? {}
            : {
                  "Content-Type": "multipart/form-data",
                  "Access-Control-Allow-Origin": "*"
              }; */

        if (setShowLoader) setShowLoader(true);

        //if (onUploadProgress) axiosConfig = { ...axiosConfig, onUploadProgress: onUploadProgress };

        axiosInstance
            .post(api_url, params)
            .then(response => {
                if (response.data?.status === 200) {
                    resolve(response);
                } else {
                    const message =
                        response.data?.message && typeof response.data?.message === "string"
                            ? response.data?.message
                            : "An error occurred while trying to process your request.";
                    if (onErrorShowMsg) showErrorMessage(message);
                    resolve(response);
                }
            })
            .catch(error => {
                onError(error);
                //reject(error);
            })
            .finally(() => {
                if (setShowLoader) {
                    setTimeout(() => {
                        setShowLoader(false);
                    }, 0);
                }
            });
    });
}

/** @deprecated use usePostInfiniteQuery instead, this is not yet fully tested */
export const usePostInfiniteQuery2 = ({
    queryKey,
    api,
    formData = {},
    onSuccess,
    useBOCommon,
    BOCommonOperation,
    keepPreviousData = false,
    enabled = true,
    onPageRequestStarted,
    onPageRequestEnded
}) => {
    const queryKeyArray = typeof queryKey === "string" ? [queryKey] : queryKey;

    return useInfiniteQuery({
        queryKey: queryKeyArray,
        queryFn: async ({ pageParam = 1 }) => {
            formData.page = pageParam;

            let tmp = null;
            if (!localStorage.getItem("token")) {
                console.error("Request cancelled, user not logged in.");
                return [];
            }

            if (onPageRequestStarted) onPageRequestStarted(formData, pageParam);

            if (!useBOCommon) tmp = await makePostRequest(api, formData);
            else tmp = await makeCommonPostRequest(BOCommonOperation, api, formData);
            const res = tmp?.data;
            if (res?.status !== 200) {
                return [];
            } else if (onPageRequestEnded) onPageRequestEnded(res, pageParam);
            return res;
        },

        getNextPageParam: (lastPage, pages) => {
            return lastPage?.data?.nextPage;
        },
        onSuccess: onSuccess,
        keepPreviousData: keepPreviousData,
        enabled: enabled
    });
};

/** @deprecated use makeApiRequest instead */
export function makeGetRequest(endpoint, data = [], onError = () => {}) {
    let api_url = `${process.env.REACT_APP_API_URL}${endpoint}?code=${process.env.REACT_APP_API_CODE}`;

    // If data is provided, append it as query parameters
    if (data) {
        const params = new URLSearchParams();

        // Loop through the data object and add each key-value pair to the URL
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                params.append(key, data[key]);
            }
        }

        // Append the parameters to the URL
        api_url += `&${params.toString()}`;
    }

    return axios
        .get(api_url)
        .then(response => {
            if (response.status === 200) {
                return response;
            } else {
                throw new Error("Error code " + response.status);
            }
        })
        .catch(error => {
            let message = "An error occurred while trying to process your request.";
            message = error?.message ? `${message} ${error.message}.` : message;

            //toast.configure();
            toast.error(message, {
                theme: "dark",
                icon: ({ theme, type }) => <img alt="error" src={toastError} />,
                autoClose: false
            });

            console.error("API Request Error:", error);
            //throw error;
            onError(error);
        });
}

/** @deprecated We now send base64 encoded files in the request body instead of uploading files directly. */
export function uploadBlobFile(fileBlob, container, setShowLoader = null) {
    return new Promise((resolve, reject) => {
        if (
            ![
                BLOB_ACCOUNT_IMAGE,
                BLOB_BOIMAGE_CONTAINER,
                BLOB_BRAND_IMAGE,
                BLOB_COMPANY_IMAGE,
                BLOB_COMPANY_IMAGE,
                BLOB_PRODUCT_CONTAINER,
                BLOB_STAFF_CONTAINER,
                BLOB_STORE_CONTAINER
            ].includes(container)
        ) {
            reject(`Container ${container} not recognized for uploading files. Try again later.`);
            return;
        }

        if (!fileBlob || !fileBlob.name) {
            reject("File selected is not recognized as compatible for upload. Try again later.");
            return;
        }

        const formData = new FormData();
        formData.append("containerName", container);
        formData.append("files", fileBlob);
        formData.append("blobName", fileBlob.name);

        makePostRequest("uploadBlobFile", formData, true, setShowLoader)
            .then(res => {
                resolve(res);
            })
            .catch(error => reject(error));
    });
}

/** @deprecated we no longer send emails directly from the frontend, we use the backend API to handle email sending. */
export function sendCommonEmail(
    emailFunctionName,
    emailFields,
    toEmail,
    setShowLoader = null,
    onError = () => {},
    onErrorShowMsg = true
) {
    const context = "sendEmail";

    const commonEndpoint = "callCommonAPI";
    const params = {
        context,
        functionName: emailFunctionName,
        paramsData: { ...emailFields, toEmail }
    };

    return new Promise((resolve, reject) => {
        const api_url = `${process.env.REACT_APP_API_URL}${commonEndpoint}?code=${process.env.REACT_APP_API_CODE}`;

        const axiosConfig = {
            "Content-Type": "multipart/form-data",
            "Access-Control-Allow-Origin": "*"
        };

        if (setShowLoader) setShowLoader(true);

        axios
            .post(api_url, params, axiosConfig)
            .then(response => {
                if (response.data?.status === 200) {
                    resolve(response);
                } else {
                    const message =
                        response.data?.message && typeof response.data?.message === "string"
                            ? response.data?.message
                            : "An error occurred while trying to process your request.";
                    if (onErrorShowMsg) showErrorMessage(message);
                    resolve(response);
                }
            })
            .catch(error => {
                onError(error);
                //reject(error);
            })
            .finally(() => {
                if (setShowLoader) {
                    setTimeout(() => {
                        setShowLoader(false);
                    }, 0);
                }
            });
    });
}
/* #endregion */
